import { Component, computed, inject, input, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { CodedStatusMessage } from '@core/models/coded-status-message.model';
import { GbeErrorCodes } from '@core/models/gbe/gbe-error-codes.enum';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { EntityIconService } from '@core/services/entity-icon/entity-icon.service';
import {
  DistinctRef,
  distinctRefs
} from '@core/services/gbe-error-parser/entity-ref-labels';
import { GbeErrorParserService } from '@core/services/gbe-error-parser/gbe-error-parser.service';
import { FormattedField } from '@core/services/gbe-error-parser/warning.model';
import { ICONS } from '@core/services/icons/icons.service';

/** One warning, ready to render: the sentence and what it left out. */
interface DeployWarningRow {
  code: string;
  title: string;
  body: string;
  fields: FormattedField[];
  refs: DistinctRef[];
}

/**
 * Renders coded deploy warnings as a repair list. The resolved body names the entity the
 * warning is about, so a renderer that kept only that body would still drop the fields.
 */
@Component({
  selector: 'app-deploy-warnings-list',
  imports: [TranslatePipe],
  templateUrl: './deploy-warnings-list.component.html',
  styles: ``
})
export class DeployWarningsListComponent {
  readonly messages = input.required<CodedStatusMessage[]>();
  /** Drops the heading where the surrounding block already counts the warnings. */
  readonly dense = input<boolean>(false);
  /**
   * The same renderer serves a deploy's `error`, which is not a warning: amber under a heading
   * reading "Error detail" tells the reader the opposite of what the label says.
   */
  readonly variant = input<'warning' | 'danger'>('warning');
  /** The message the warnings are listed under, when the list belongs to one. */
  readonly subjectUid = input<string | null>(null);

  protected readonly ICONS = ICONS;
  protected readonly expanded = signal(true);

  /**
   * Only the tint moves with the variant. The text stays `text-text-primary` either way, which
   * is what makes both boxes readable: a soft fill at 94% lightness carries dark text, and the
   * solid `--danger` needed to carry white instead scored below AA at this size.
   */
  protected readonly boxClass = computed(() =>
    this.variant() === 'danger'
      ? 'bg-danger-soft border-danger'
      : 'bg-warning-soft border-warning'
  );

  private readonly parser = inject(GbeErrorParserService);
  private readonly translate = inject(TranslateService);
  private readonly entityIcons = inject(EntityIconService);
  private readonly currentLangService = inject(CurrentLangService);

  /**
   * Messages name no entity, so one problem hitting many routes resolves to the same sentence
   * many times: those rows become one, carrying every route as a reference. Rows whose
   * leftover fields differ stay apart, since a merged row could show only one set of values.
   */
  protected readonly rows = computed<DeployWarningRow[]>(() => {
    this.currentLangService.currentLang();
    const byContent = new Map<string, DeployWarningRow>();
    for (const message of this.messages()) {
      for (const view of this.parser.resolve(message)) {
        for (const occurrence of view.occurrences) {
          const refs = distinctRefs(occurrence.refs, this.translate);
          const key = JSON.stringify([view.title, occurrence.body, occurrence.fields]);
          const row = byContent.get(key);
          if (row === undefined) {
            byContent.set(key, {
              code: message.errorCode,
              title: view.title,
              body: occurrence.body,
              fields: occurrence.fields,
              refs
            });
          } else {
            row.refs.push(
              ...refs.filter(
                (ref) =>
                  !row.refs.some(
                    (seen) => seen.entityType === ref.entityType && seen.uid === ref.uid
                  )
              )
            );
          }
        }
      }
    }
    return [...byContent.values()].map((row) => this.withRouteValidationBody(row));
  });

  /**
   * The catalog sentence says the message carries one unvalidated route, which misreads in
   * two places: a route sheet or GedMouv message *is* the route, and a merged row can carry
   * several. The wording is chosen here because only the merged row knows how many routes
   * it holds, and only the list knows which message it sits under.
   */
  private withRouteValidationBody(row: DeployWarningRow): DeployWarningRow {
    if (row.code !== GbeErrorCodes.DEPLOY_ROUTE_NOT_VALIDATED) return row;
    const subject = this.subjectUid();
    if (row.refs.length === 1 && subject !== null && row.refs[0].uid === subject) {
      return {
        ...row,
        body: this.translate.instant(
          'features.plan.deploy.warnings.route_not_validated_self'
        )
      };
    }
    if (row.refs.length > 1) {
      return {
        ...row,
        body: this.translate.instant(
          'features.plan.deploy.warnings.route_not_validated_other'
        )
      };
    }
    return row;
  }

  protected entityIcon(entityType: string): string {
    return this.entityIcons.getEntityIcon(entityType);
  }
}
